import store from 'commons/store/Store';
import {
  PUBLISHING_SCOPE,
  type PublishingScope,
} from 'commons/publishing/publishingScope';
import {
  SAMPLE_ORDER_SCOPES,
  sampleOrderAvailable,
} from './sampleOrderAvailability';

/** Kept apart from the store so the rule below stays pure. */
export interface SampleOrderChannelAvailability {
  showroomEnabled: boolean;
  mpJourneyAvailable: boolean;
  hasShop: boolean;
  shopJourneyAvailable: boolean;
}

/**
 * The sales channels a partner can order samples from.
 *
 * Owning the channel is not enough — the *new* publishing journey has to be available for its scope,
 * because the selection list reads publishing-core compositions and a scope still on flexi has none.
 *
 * `scopes` is the staged rollout, a separate pass so the ownership rules stay readable. Its domain
 * half sits in the wrapper below, the only place a locale can be read.
 */
export const pickSampleOrderChannels = (
  {
    showroomEnabled,
    mpJourneyAvailable,
    hasShop,
    shopJourneyAvailable,
  }: SampleOrderChannelAvailability,
  scopes: readonly string[] = SAMPLE_ORDER_SCOPES,
): PublishingScope[] => {
  const channels: PublishingScope[] = [];

  if (showroomEnabled && mpJourneyAvailable) {
    channels.push(PUBLISHING_SCOPE.MP);
  }

  if (hasShop && shopJourneyAvailable) {
    channels.push(PUBLISHING_SCOPE.SHOP);
  }

  return channels.filter((scope) => scopes.includes(scope.key));
};

export const availableSampleOrderChannels = (): PublishingScope[] =>
  sampleOrderAvailable()
    ? pickSampleOrderChannels({
        showroomEnabled: !!store.getters['settings/getSetting']('MP_ENABLED'),
        mpJourneyAvailable:
          !!store.getters['settings/newPublishingJourneyAvailabilityMp'],
        hasShop: store.state.user.pointsOfSale.some(
          (pos) => pos.type === 'SHOP',
        ),
        shopJourneyAvailable:
          !!store.getters['settings/newPublishingJourneyAvailabilityShop'],
      })
    : [];

/** What the dashboard and nav triggers hide on. */
export const canStartSampleOrder = (): boolean =>
  availableSampleOrderChannels().length > 0;

/** Kept apart from the store for the same reason as `pickSampleOrderChannels`: it is worth a test. */
export const includesShopChannel = (channels: PublishingScope[]): boolean =>
  channels.some((scope) => scope.key === PUBLISHING_SCOPE.SHOP.key);

/**
 * What the Shop Settings trigger hides on: standing in a shop rules out the Showroom half of
 * `canStartSampleOrder`.
 */
export const canStartShopSampleOrder = (): boolean =>
  includesShopChannel(availableSampleOrderChannels());
